/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { labelStyles } from '@zapier/style-encapsulation';
import { Icon, VisuallyHidden } from '@zapier/design-system';

type Props = {
  /*
   * `aria-label` for the `IconButton` if `label` isn't supplied.
   */
  ariaLabel?: string;
  /*
   * `aria-hidden` for the `IconButton`.
   */
  ariaHidden?: boolean;
  /*
   * Indicates whether or not `IconButton` is disabled.
   */
  disabled?: boolean;
  /**
   * `id` of the `input` that this component points to.
   */
  id?: string;
  /**
   * Indicates whether the user is currently searching.
   */
  isSearching: boolean;
  /*
   * Label for the `IconButton`.
   */
  label?: string;
  /**
   * Refers to the size of the topbar.
   */
  size?: 'small' | 'medium';
};

const Styles = labelStyles('SearchTogglerInApp', {
  root: (size: Props['size']) => css`
    height: 36px;
    border-radius: var(--zds-radius-small, 3px);
    display: flex;
    align-items: center;
    gap: var(--zds-space-4, 4px);
    padding: ${size === 'medium'
      ? ' 10px var(--zds-space-12, 12px)'
      : '10px var(--zds-space-4, 4px)'};
    justify-content: center;
    color: var(--zds-text-default, #413735);
    font-weight: 500;

    &:hover,
    &:focus {
      color: var(--zds-text-default, #413735);
      background-color: var(--zds-background-stronger, #f5f3eb);
    }
  `,
});

/**
 * @private
 * Button that controls whether or not the search form is displayed.
 */
const SearchToggler = (props: Props) => {
  return (
    <label css={Styles.root(props.size)} htmlFor={props.id} data-zds>
      <Icon name={props.isSearching ? 'formX' : 'navSearch'} size={24} />
      {props.size !== 'small' ? (
        'Search'
      ) : (
        <VisuallyHidden>Search</VisuallyHidden>
      )}
    </label>
  );
};

export default SearchToggler;
